import z from 'zod';

/**
 * Medical History Configuration Types
 *
 * These types define the contract for medical history quick picks configuration,
 * including medical conditions, allergies, medications, and in-house medications.
 */

/**
 * Medical condition quick pick item
 */
export interface MedicalConditionQuickPick {
  code?: string;
  display: string;
}

export const MedicalConditionQuickPickSchema: z.ZodType<MedicalConditionQuickPick, z.ZodTypeDef, unknown> = z.object({
  code: z.string().optional(),
  display: z.string().min(1),
});

/**
 * Allergy quick pick item
 */
export interface AllergyQuickPick {
  name: string;
  id?: number;
}

export const AllergyQuickPickSchema: z.ZodType<AllergyQuickPick, z.ZodTypeDef, unknown> = z.object({
  name: z.string().min(1),
  id: z.number().optional(),
});

/**
 * Medication history quick pick item
 */
export interface MedicationHistoryQuickPick {
  name: string;
  strength?: string;
  id: number;
}

export const MedicationHistoryQuickPickSchema: z.ZodType<MedicationHistoryQuickPick, z.ZodTypeDef, unknown> = z.object({
  name: z.string().min(1),
  strength: z.string().optional(),
  id: z.number(),
});

/**
 * In-house medication quick pick item.
 * Must have either dosespotId or ndc or both (enforced by InHouseMedicationQuickPickSchema).
 */
export interface InHouseMedicationQuickPick {
  name: string;
  dosespotId?: number;
  ndc?: string;
  dose?: number;
  units?: string;
  route?: string;
  instructions?: string;
}

export const InHouseMedicationQuickPickSchema: z.ZodType<InHouseMedicationQuickPick, z.ZodTypeDef, unknown> = z
  .object({
    name: z.string().min(1),
    dosespotId: z.number().optional(),
    ndc: z.string().optional(),
    dose: z.number().optional(),
    units: z.string().optional(),
    route: z.string().optional(),
    instructions: z.string().optional(),
  })
  .refine((data) => data.dosespotId !== undefined || data.ndc !== undefined, {
    message: 'Must have either dosespotId or ndc or both',
  });

/**
 * Medical conditions section
 */
export interface MedicalConditionsSection {
  quickPicks: MedicalConditionQuickPick[];
}

/**
 * Allergies section
 */
export interface AllergiesSection {
  quickPicks: AllergyQuickPick[];
}

/**
 * Medications section
 */
export interface MedicationsSection {
  quickPicks: MedicationHistoryQuickPick[];
}

/**
 * In-house medications section
 */
export interface InHouseMedicationsSection {
  quickPicks: InHouseMedicationQuickPick[];
}

/**
 * Full medical history configuration
 */
export interface MedicalHistoryConfig {
  medicalConditions: MedicalConditionsSection;
  allergies: AllergiesSection;
  medications: MedicationsSection;
  inHouseMedications: InHouseMedicationsSection;
}

export const MedicalHistoryConfigSchema: z.ZodType<MedicalHistoryConfig, z.ZodTypeDef, unknown> = z.object({
  medicalConditions: z.object({
    quickPicks: z.array(MedicalConditionQuickPickSchema).default([]),
  }),
  allergies: z.object({
    quickPicks: z.array(AllergyQuickPickSchema).default([]),
  }),
  medications: z.object({
    quickPicks: z.array(MedicationHistoryQuickPickSchema).default([]),
  }),
  inHouseMedications: z.object({
    quickPicks: z.array(InHouseMedicationQuickPickSchema).default([]),
  }),
});
